import {
  useEffect, useMemo, useState, useCallback, type ReactNode,
} from 'react';
import type { AnalyticsSnippet } from '@segment/analytics-next';
import { useSelector } from 'react-redux';
import { ErrorManagement } from '@printdeal/error-management/react';
import { useConsent } from '@printdeal/consent-management/react';
import SegmentContext from './SegmentContext';
import { selectors as authSelectors } from '../../store/slices/authSlice';
// @ts-expect-error
import { selectors as customerSelectors } from '../../store/slices/customerSlice';

declare global {
  interface Window {
    analytics: AnalyticsSnippet;
  }
}

interface Props {
  children?: ReactNode;
}

const SegmentProvider = ({ children }: Props) => {
  const consentData = useConsent();
  const [isConsentReady, setIsConsentReady] = useState<boolean>(false);

  const [isSegmentAnalyticsReady, setIsSegmentAnalyticsReady] = useState<boolean>(false);
  const isAuthLoading = useSelector(authSelectors.isAuthLoading);
  const isLoggedIn = useSelector(authSelectors.isLoggedIn);
  const accountId: string = useSelector(customerSelectors.getAccountId);

  useEffect(() => {
    function onConsentReady() {
      setIsConsentReady(true);
    }

    function onSegmentAnalyticsReady() {
      setIsSegmentAnalyticsReady(true);
    }

    window.addEventListener('CookiebotOnConsentReady', onConsentReady);
    window.addEventListener('SegmentAnalyticsReady', onSegmentAnalyticsReady);

    return () => {
      window.removeEventListener('CookiebotOnConsentReady', onConsentReady);
      window.removeEventListener('SegmentAnalyticsReady', onSegmentAnalyticsReady);
    };
  }, []);

  const identifyLoggedInUser = useCallback((userId: string) => {
    window.analytics.identify(userId, {}, { context: { consent: consentData } });
  }, [consentData]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    try {
      if (!isAuthLoading && consentData?.statistics && consentData?.marketing && isSegmentAnalyticsReady && accountId) {
        identifyLoggedInUser(accountId);
      }
    } catch (error) {
      ErrorManagement.exception({
        exception: error,
        tags: {
          fileLocation: 'SegmentProvider',
        },
        fingerprint: ['segment-provider'],
      });
    }
  }, [isAuthLoading, isLoggedIn, consentData, isSegmentAnalyticsReady, accountId, identifyLoggedInUser]);

  const traitsContext = useMemo(() => ({
    isSegmentAnalyticsReady,
    consentData,
    isConsentReady,
  }), [isSegmentAnalyticsReady, consentData, isConsentReady]);

  return (
    <SegmentContext.Provider value={traitsContext}>
      {children}
    </SegmentContext.Provider>
  );
};

export default SegmentProvider;
